import { Box, Flex, Grid, Heading, Stack, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import { useMemo } from 'react';
import * as m from 'motion/react-m';

import { ChatMock } from '../DeviceMockups/ChatMock';
import { DiscoverMock, type GameIconMap, type GameInfo } from '../DeviceMockups/DiscoverMock';
import { PublishedPostMock } from '../DeviceMockups/PublishedPostMock';

const ChevronRight = ({ color }: { color: string }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

type StepCardProps = {
  number: number;
  title: string;
  description: string;
  image: React.ReactNode;
  accentColor: string;
  delay: number;
};

const StepCard = ({ number, title, description, image, accentColor, delay }: StepCardProps) => (
  <Flex
    direction="column"
    alignItems="center"
    textAlign="center"
    gap={6}
    flex={1}
    position="relative"
    _hover={{
      '& .hiw-phone': { transform: 'translateY(-10px)' },
      '& .hiw-glow': { opacity: 0.8 },
    }}
    asChild
  >
    <m.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.5 }}
    >
      {/* Stage header: on mobile/tablet the number sits inline with the title;
          on desktop it's a lone badge sitting on the connector line. */}
      <Flex alignItems="center" justifyContent="center" gap={3}>
        <Flex
          position="relative"
          zIndex={2}
          w={{ base: '44px', lg: '56px' }}
          h={{ base: '44px', lg: '56px' }}
          flexShrink={0}
          borderRadius="full"
          alignItems="center"
          justifyContent="center"
          fontWeight="900"
          fontSize={{ base: 'md', lg: 'lg' }}
          color="white"
          overflow="hidden"
          _before={{
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: 'full',
            background:
              'linear-gradient(180deg, rgba(255,255,255,0.10) 0%, transparent 55%)',
            pointerEvents: 'none',
          }}
          style={{
            background: accentColor,
            boxShadow: [
              // gap that punches the badge cleanly through the connector line
              '0 0 0 5px hsl(228, 11.6%, 8%)',
              // thin accent-tinted rim instead of a hard dark outline
              '0 0 0 6px rgba(255,255,255,0.08)',
              // inner top sheen + bottom depth → looks like a polished coin
              'inset 0 1px 1px rgba(255,255,255,0.14)',
              'inset 0 -3px 6px rgba(0,0,0,0.28)',
              // soft coloured glow lifting it off the background
              '0 8px 22px -6px rgba(0,0,0,0.55)',
            ].join(', '),
          }}
        >
          <Box as="span" position="relative" zIndex={1} textShadow="0 1px 2px rgba(0,0,0,0.35)">
            {number}
          </Box>
        </Flex>
        {/* Inline title - mobile/tablet only */}
        <Heading
          display={{ base: 'block', lg: 'none' }}
          as="h3"
          fontWeight="800"
          fontSize="lg"
          lineHeight="short"
          textAlign="left"
        >
          {title}
        </Heading>
      </Flex>

      {/* Floating phone on an accent glow - no flat box, staggered wave */}
      <Box
        position="relative"
        w="100%"
        h="540px"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Box position="relative" display="flex" justifyContent="center" alignItems="center">
          {/* Подложка под телефоном: слабое тёмное пятно, отделяющее макет от фона. Раньше это
              был акцентный ореол — вместе с цветной тенью корпуса телефон превращался в лампу. */}
          <Box
            className="hiw-glow"
            position="absolute"
            w="300px"
            h="360px"
            borderRadius="full"
            opacity={0.5}
            transition="opacity 0.4s ease-out"
            pointerEvents="none"
            style={{
              background: `radial-gradient(circle, ${accentColor}14 0%, ${accentColor}07 46%, transparent 72%)`,
            }}
          />
          <Box
            className="hiw-phone"
            position="relative"
            zIndex={1}
            transition="transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)"
          >
            {/* Gradient sheen border hugging the phone */}
            <Box
              borderRadius="44px"
              p="1.5px"
              style={{
                background: `linear-gradient(150deg, ${accentColor}80 0%, ${accentColor}00 38%, ${accentColor}00 62%, rgba(142,123,255,0.5) 100%)`,
              }}
            >
              {image}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Caption - title shown below the phone on desktop only (inline in header on mobile) */}
      <Stack gap={2} maxW="320px" px={2}>
        <Heading
          display={{ base: 'none', lg: 'block' }}
          as="h3"
          fontWeight="800"
          fontSize={{ base: 'lg', md: 'xl' }}
          lineHeight="short"
        >
          {title}
        </Heading>
        <Text fontWeight="400" fontSize="sm" color="fg.muted" lineHeight="tall">
          {description}
        </Text>
      </Stack>
    </m.div>
  </Flex>
);

type HowItWorksProps = {
  accentColor: string;
  gameIcons: GameIconMap;
  pickerGames: GameInfo[];
  /** The game featured in the step-1 "create post" mock (current game, or Apex on index). */
  featuredGame?: GameInfo;
  /** Which picker tile the step-1 mock highlights (current game slug, or 'apex-legends'). */
  featuredSlug?: string;
};

export const HowItWorks = ({
  accentColor,
  gameIcons,
  pickerGames,
  featuredGame,
  featuredSlug,
}: HowItWorksProps) => {
  const { t } = useLingui();

  const steps = useMemo(
    () => [
      {
        image: (
          <PublishedPostMock
            game={featuredGame ?? gameIcons['apex-legends']}
            games={pickerGames}
            featuredSlug={featuredSlug}
          />
        ),
        title: t`v3.how.step1.title`,
        description: t`v3.how.step1.desc`,
      },
      {
        image: <DiscoverMock gameIcons={gameIcons} />,
        title: t`v3.how.step2.title`,
        description: t`v3.how.step2.desc`,
      },
      {
        image: <ChatMock />,
        title: t`v3.how.step3.title`,
        description: t`v3.how.step3.desc`,
      },
    ],
    [t, gameIcons, pickerGames, featuredGame, featuredSlug]
  );

  return (
    <Box as="section" aria-label="How It Works" id="how-it-works" w="100%" position="relative">
      {/* Soft section accent glow */}
      <Box
        position="absolute"
        top="-30px"
        left="50%"
        transform="translateX(-50%)"
        w="640px"
        maxW="100%"
        h="300px"
        pointerEvents="none"
        zIndex={0}
        style={{ background: `radial-gradient(ellipse at center, ${accentColor}0b 0%, transparent 70%)` }}
      />
      <Box textAlign="center" mb={{ base: 8, lg: 12 }} position="relative">
        <Text
          fontSize="xs"
          fontWeight="700"
          color="purple.300"
          textTransform="uppercase"
          letterSpacing="0.15em"
          mb={3}
        >
          {t`v3.how.label`}
        </Text>
        <Heading
          fontWeight="900"
          lineHeight="1.05"
          fontSize={{ base: '3xl', lg: '5xl' }}
          as="h2"
          mb={3}
          textTransform="uppercase"
          letterSpacing="-0.02em"
        >
          {t`v3.how.title`}
        </Heading>
        <Text fontSize="md" color="fg.muted" maxW="520px" mx="auto">
          {t`v3.how.subtitle`}
        </Text>
      </Box>

      <Box position="relative">
        {/* Progress connector line through the number badges (desktop) */}
        <Box
          display={{ base: 'none', lg: 'block' }}
          position="absolute"
          top="28px"
          left="16.6%"
          right="16.6%"
          h="2px"
          zIndex={0}
          pointerEvents="none"
          style={{
            background: `linear-gradient(90deg, transparent, ${accentColor}22 12%, ${accentColor}22 88%, transparent)`,
          }}
        />
        {/* Arrows between the steps */}
        {[33.3, 66.6].map((left) => (
          <Box
            key={left}
            display={{ base: 'none', lg: 'block' }}
            position="absolute"
            top="28px"
            left={`${left}%`}
            transform="translate(-50%, -50%)"
            zIndex={1}
            pointerEvents="none"
            opacity={0.85}
          >
            <ChevronRight color={accentColor} />
          </Box>
        ))}
        <Grid
          templateColumns={{ base: '1fr', lg: 'repeat(3, 1fr)' }}
          gap={{ base: 12, lg: 8 }}
          alignItems="flex-start"
          position="relative"
        >
          {steps.map((step, index) => (
            <StepCard
              key={step.title}
              number={index + 1}
              title={step.title}
              description={step.description}
              image={step.image}
              accentColor={accentColor}
              delay={index * 0.15}
            />
          ))}
        </Grid>
      </Box>
    </Box>
  );
};
